import { Icon } from '@buggy/shared'
import { Dropdown, MenuProps } from 'antd'
import React, { FunctionComponent } from 'react'
import styled, { useTheme } from 'styled-components'
import { Duration } from '@/components/common/duration-select'
import useKolDetailRoute from '@/components/kol/hooks/use-kol-detail-route'
import { KolDetailDuration } from '@/hooks/use-authorization/routes'
import { I18nId } from '@/i18n/config'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli } from '@/utils/ampli'

const durationI18nMap: Record<KolDetailDuration, I18nId> = {
  [Duration.ThreeMonths]: 'kol:duration_3mo',
  [Duration.SixMonths]: 'kol:duration_6mo',
}

interface DurationDropdownProps {
  kolInfo?: KolSchemaModel
  value: KolDetailDuration
  disabledSixMonths: boolean
  enable3MonthDuration: boolean
}

const DurationDropdown: FunctionComponent<DurationDropdownProps> = ({
  value,
  kolInfo,
  disabledSixMonths,
  enable3MonthDuration,
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const { searchParams, setSearchParams } = useKolDetailRoute()

  const handleSelect = async (value: KolDetailDuration): Promise<void> => {
    if (!kolInfo || !searchParams.success) {
      return
    }

    ampli.chooseKolDetailPeriodFilter({
      kolCategory: kolInfo.customizedTagsNames,
      kolId: kolInfo.id,
      kolName: kolInfo.name,
      kolSection: 'Overview',
      periodRangeType:
        value === Duration.ThreeMonths ? 'Recent 3 months' : 'Recent 6 months',
      platform: searchParams.data.platform,
    })

    await setSearchParams({
      ...searchParams.data,
      duration: value,
    })
  }

  const items: MenuProps['items'] = [
    ...(enable3MonthDuration
      ? [
          {
            key: Duration.ThreeMonths,
            onClick: (): Promise<void> => handleSelect(Duration.ThreeMonths),
            label: <Option>{formatMessage({ id: 'kol:duration_3mo' })}</Option>,
          },
        ]
      : []),
    {
      key: Duration.SixMonths,
      disabled: disabledSixMonths,
      icon: disabledSixMonths ? <Icon name='lock' /> : undefined,
      onClick: (): Promise<void> => handleSelect(Duration.SixMonths),
      label: <Option>{formatMessage({ id: 'kol:duration_6mo' })}</Option>,
    },
  ]

  return (
    <Dropdown menu={{ items }} trigger={['click']}>
      <Flex>
        {formatMessage({ id: durationI18nMap[value] })}
        <Icon
          fontSize={10}
          name='chevron-down'
          stroke={theme.colors.text.secondary}
        />
      </Flex>
    </Dropdown>
  )
}

export default DurationDropdown

const Flex = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  font-size: 14px;
  font-weight: 700;
  padding: 5px 0;
  color: ${({ theme }): string => theme.colors.text.secondary};
  cursor: pointer;
`

const Option = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
`
